import React, { useState, useMemo, useEffect } from "react";
import {
  DetailsList,
  DetailsListLayoutMode,
  SelectionMode,
  IColumn,
  PrimaryButton,
  DefaultButton,
  Icon,
  Stack,
  Text,
  Modal,
  IconButton,
  Spinner,
  SpinnerSize,
  Pivot,
  PivotItem,
} from "@fluentui/react";
import ESignDocumentComponent from "./components/ESignDocument";
import { useUser } from "../../../Context/UserContext";
import {
  getESignDocuments,
  getESignDocumentPages,
  saveESignDocument,
  dismissSuggestion,
} from "../../../Service/Services";
import "./styles/main.css";

interface SignedDocument {
  id: number;
  fileUrl: string;
  name: string;
}

interface ESignDocument {
  id: number;
  title: string;
  status: "Signed" | "Pending" | "Expired" | "Not Signed";
  latestSignedDate: string | null;
  latestExpiresDate: string | null;
  signedDocuments: SignedDocument[];
}

const ESign: React.FC = () => {
  const { user, loading: isUserLoading } = useUser();
  const [selectedDocument, setSelectedDocument] =
    useState<ESignDocument | null>(null);
  const [isSignModalOpen, setIsSignModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<{
    type: "bad-request" | "server-error" | "generic";
  } | null>(null);
  const [documentHtmlContent, setDocumentHtmlContent] = useState<string>("");
  const [isPagesLoading, setIsPagesLoading] = useState(false);

  const [documents, setDocuments] = useState<ESignDocument[]>([]);

  useEffect(() => {
    if (isUserLoading || !user?.LoginName) return;

    const loginName = user.LoginName.includes("\\")
      ? user.LoginName.split("\\")[1]
      : user.LoginName;

    const fetchDocuments = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const response = await getESignDocuments(
          loginName,
          user.RoleID,
          user.RegionID ?? 0,
        );
        const data = response.data;
        const items: any[] = Array.isArray(data) ? data : (data?.value ?? []);
        setDocuments(
          items.map((item: any) => ({
            id: item.ID ?? item.Id ?? item.id,
            title: item.Title ?? item.title,
            status: item.DocumentStatus?.Status ?? item.status ?? item.Status,
            latestSignedDate:
              item.DocumentStatus?.LatestSignedDate ??
              item.LatestSignedDate ??
              null,
            latestExpiresDate:
              item.DocumentStatus?.LatestExpiresDate ??
              item.LatestExpiresDate ??
              null,
            signedDocuments: (item.DocumentStatus?.SignedDocuments ?? []).map(
              (sd: any) => ({
                id: sd.ID,
                fileUrl: sd.FileUrl,
                name: sd.Name,
              }),
            ),
          })),
        );
      } catch (err: any) {
        const status = err?.response?.status;
        if (status >= 400 && status < 500) {
          setError({ type: "bad-request" });
        } else if (status >= 500) {
          setError({ type: "server-error" });
        } else {
          setError({ type: "generic" });
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchDocuments();
  }, [user?.LoginName, user?.RoleID, user?.RegionID, isUserLoading]);

  const signedCount = useMemo(
    () => documents.filter((doc) => doc.status === "Signed").length,
    [documents],
  );

  const totalCount = documents.length;
  const remainingCount = totalCount - signedCount;

  const handleSignNow = async (doc: ESignDocument) => {
    setSelectedDocument(doc);
    setDocumentHtmlContent("");
    setIsSignModalOpen(true);
    setIsPagesLoading(true);
    try {
      const response = await getESignDocumentPages(doc.id);
      const data = response.data;
      const pages: any[] = Array.isArray(data) ? data : (data?.value ?? []);
      setDocumentHtmlContent(
        pages.map((p: any) => p.pageContent ?? "").join(""),
      );
    } catch {
      setDocumentHtmlContent("<p>Failed to load document content.</p>");
    } finally {
      setIsPagesLoading(false);
    }
  };

  const handleCloseModal = () => {
    setIsSignModalOpen(false);
    setSelectedDocument(null);
  };

  const handleSubmitSignature = async (signatureData: string) => {
    if (!selectedDocument || !user) return;
    setIsSubmitting(true);
    try {
      const loginName = user.LoginName.includes("\\")
        ? user.LoginName.split("\\")[1]
        : user.LoginName;

      await saveESignDocument({
        signatureImage: signatureData,
        documentID: selectedDocument.id,
        userLoginName: loginName,
        userName: user.FullName,
        region: user.Region ?? "",
        role: user.Role ?? "",
        suggestionID: 100,
        ruleID: 200,
      });

      const suggestionIdParam = new URLSearchParams(window.location.search).get(
        "suggestionId",
      );
      if (suggestionIdParam) {
        const suggestionId = parseInt(suggestionIdParam, 10);
        if (!isNaN(suggestionId)) {
          await dismissSuggestion(suggestionId);
        }
      }

      setDocuments((prev) =>
        prev.map((doc) =>
          doc.id === selectedDocument.id
            ? {
                ...doc,
                status: "Signed" as const,
                latestSignedDate: new Date().toISOString().split("T")[0],
                signedDocuments: doc.signedDocuments,
              }
            : doc,
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = () => {
    handleCloseModal();
  };

  const handleView = (doc: ESignDocument) => {
    const fileUrl = doc.signedDocuments[0]?.fileUrl;
    if (fileUrl) {
      window.open(fileUrl, "_blank", "noopener,noreferrer");
    }
  };

  const getStatusClassName = (status: string) => {
    switch (status) {
      case "Signed":
        return "esign-status-badge esign-status-signed";
      case "Expired":
        return "esign-status-badge esign-status-expired";
      case "Not Signed":
      case "Pending":
      default:
        return "esign-status-badge esign-status-pending";
    }
  };

  const signedColumns: IColumn[] = [
    {
      key: "title",
      name: "Title",
      fieldName: "title",
      minWidth: 200,
      maxWidth: 350,
      isResizable: true,
    },
    {
      key: "status",
      name: "Status",
      fieldName: "status",
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: ESignDocument) => (
        <span className={getStatusClassName(item.status)}>{item.status}</span>
      ),
    },
    {
      key: "signedDate",
      name: "Signed Date",
      fieldName: "latestSignedDate",
      minWidth: 100,
      maxWidth: 130,
      onRender: (item: ESignDocument) => (
        <span>{item.latestSignedDate || "-"}</span>
      ),
    },
    {
      key: "expiryDate",
      name: "Expiry Date",
      fieldName: "latestExpiresDate",
      minWidth: 100,
      maxWidth: 130,
      onRender: (item: ESignDocument) => (
        <span>{item.latestExpiresDate || "-"}</span>
      ),
    },
    {
      key: "action",
      name: "Action",
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: ESignDocument) => (
        <DefaultButton
          text="View"
          iconProps={{ iconName: "Download" }}
          onClick={() => handleView(item)}
          disabled={!item.signedDocuments[0]?.fileUrl}
          styles={{
            root: {
              backgroundColor: "#107c10 !important",
              color: "white !important",
              borderRadius: "4px",
              border: "none",
            },
            rootHovered: {
              backgroundColor: "#0b6a0b !important",
              color: "white !important",
            },
            icon: { color: "white !important" },
          }}
        />
      ),
    },
  ];

  const columns: IColumn[] = [
    {
      key: "title",
      name: "Title",
      fieldName: "title",
      minWidth: 200,
      maxWidth: 350,
      isResizable: true,
    },
    {
      key: "status",
      name: "Status",
      fieldName: "status",
      minWidth: 100,
      maxWidth: 120,
      onRender: (item: ESignDocument) => (
        <span className={getStatusClassName(item.status)}>{item.status}</span>
      ),
    },
    {
      key: "signedDate",
      name: "Signed Date",
      fieldName: "latestSignedDate",
      minWidth: 100,
      maxWidth: 130,
      onRender: (item: ESignDocument) => (
        <span>{item.latestSignedDate || "-"}</span>
      ),
    },
    {
      key: "expiryDate",
      name: "Expiry Date",
      fieldName: "latestExpiresDate",
      minWidth: 100,
      maxWidth: 130,
      onRender: (item: ESignDocument) => (
        <span>{item.latestExpiresDate || "-"}</span>
      ),
    },
    {
      key: "action",
      name: "Action",
      minWidth: 120,
      maxWidth: 150,
      onRender: (item: ESignDocument) =>
        item.status === "Signed" ? (
          <DefaultButton
            text="View"
            iconProps={{ iconName: "Download" }}
            onClick={() => handleView(item)}
            styles={{
              root: {
                backgroundColor: "#107c10 !important",
                color: "white !important",
                borderRadius: "4px",
                border: "none",
              },
              rootHovered: {
                backgroundColor: "#0b6a0b !important",
                color: "white !important",
              },
              icon: { color: "white !important" },
            }}
          />
        ) : (
          <PrimaryButton
            text="Sign Now"
            iconProps={{ iconName: "EditNote" }}
            onClick={() => handleSignNow(item)}
            styles={{
              root: {
                backgroundColor: "#00345D !important",
                color: "white !important",
                borderRadius: "4px",
                border: "none",
              },
              rootHovered: {
                backgroundColor: "#002a4a !important",
                color: "white !important",
              },
              icon: { color: "white !important" },
            }}
          />
        ),
    },
  ];

  return (
    <div className="esign-container">
      {/* Sign Document Modal */}
      <Modal
        isOpen={isSignModalOpen}
        onDismiss={handleCloseModal}
        isBlocking={false}
        containerClassName="esign-modal-container"
      >
        <div className="esign-modal-header">
          <Text variant="xLarge" className="esign-modal-title">
            Sign Document
          </Text>
          <IconButton
            iconProps={{ iconName: "Cancel" }}
            ariaLabel="Close"
            onClick={handleCloseModal}
          />
        </div>
        <div className="esign-modal-body">
          {isPagesLoading ? (
            <Spinner size={SpinnerSize.large} label="Loading document..." />
          ) : (
            selectedDocument && (
              <ESignDocumentComponent
                document={selectedDocument}
                htmlContent={documentHtmlContent}
                onContinue={handleContinue}
                onSubmit={handleSubmitSignature}
                isSubmitting={isSubmitting}
              />
            )
          )}
        </div>
      </Modal>

      {/* Documents List */}
      {isLoading ? (
        <div className="esign-card esign-skeleton-card">
          <div className="esign-skeleton esign-skeleton-title" />
          <div className="esign-skeleton-progress-card">
            <div className="esign-skeleton esign-skeleton-label" />
            <div className="esign-skeleton esign-skeleton-bar" />
            <div className="esign-skeleton-legend">
              <div className="esign-skeleton esign-skeleton-chip" />
              <div className="esign-skeleton esign-skeleton-chip" />
            </div>
          </div>
          <div className="esign-skeleton esign-skeleton-tabs" />
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="esign-skeleton-row">
              <div className="esign-skeleton esign-skeleton-cell esign-skeleton-cell-wide" />
              <div className="esign-skeleton esign-skeleton-cell esign-skeleton-cell-short" />
              <div className="esign-skeleton esign-skeleton-cell esign-skeleton-cell-short" />
              <div className="esign-skeleton esign-skeleton-cell esign-skeleton-cell-short" />
              <div className="esign-skeleton esign-skeleton-cell esign-skeleton-cell-btn" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div
          className={`esign-card esign-error-card esign-error-card--${error.type === "bad-request" ? "warn" : "danger"}`}
        >
          <div
            className={`esign-error-icon-wrap ${error.type === "bad-request" ? "esign-error-icon-wrap--warn" : "esign-error-icon-wrap--danger"}`}
          >
            <Icon
              iconName={
                error.type === "bad-request" ? "BlockedSite" : "ServerProcesses"
              }
              className="esign-error-icon"
            />
          </div>
          <Text variant="xLarge" className="esign-error-title">
            {error.type === "bad-request" ? "Invalid Request" : "Server Error"}
          </Text>
          <Text variant="medium" className="esign-error-subtitle">
            {error.type === "bad-request"
              ? "The request could not be processed. Please check your account details or contact support if the problem persists."
              : "Something went wrong on our end. Please try again later or contact support if the issue continues."}
          </Text>
        </div>
      ) : documents.length > 0 ? (
        <div className="esign-card">
          <Text variant="xLarge" className="esign-card-title">
            E-Sign Documents
          </Text>

          {/* Progress Card */}
          <div className="esign-progress-card">
            <Stack
              horizontal
              horizontalAlign="space-between"
              verticalAlign="center"
              className="esign-progress-header"
            >
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 8 }}
              >
                <Icon
                  iconName="DocumentApproval"
                  className="esign-progress-icon"
                />
                <Text variant="mediumPlus" className="esign-progress-title">
                  Signing Progress
                </Text>
              </Stack>
              <Text variant="mediumPlus" className="esign-progress-fraction">
                <span className="esign-progress-signed">{signedCount}</span>
                <span className="esign-progress-separator">
                  {" "}
                  / {totalCount} signed
                </span>
              </Text>
            </Stack>
            <div className="esign-progress-bar-track">
              <div
                className="esign-progress-bar-fill"
                style={{
                  width:
                    totalCount > 0
                      ? `${(signedCount / totalCount) * 100}%`
                      : "0%",
                }}
              />
            </div>
            <Stack
              horizontal
              tokens={{ childrenGap: 24 }}
              className="esign-progress-legend"
            >
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 6 }}
              >
                <span className="esign-legend-dot esign-legend-signed" />
                <Text variant="small">{signedCount} Signed</Text>
              </Stack>
              <Stack
                horizontal
                verticalAlign="center"
                tokens={{ childrenGap: 6 }}
              >
                <span className="esign-legend-dot esign-legend-pending" />
                <Text variant="small">{remainingCount} Not Signed</Text>
              </Stack>
            </Stack>
          </div>

          {/* Tabbed document lists */}
          <Pivot>
            <PivotItem
              headerText={`Not Signed (${remainingCount})`}
              itemIcon="EditNote"
            >
              {documents.filter((d) => d.status !== "Signed").length > 0 ? (
                <DetailsList
                  items={documents.filter((d) => d.status !== "Signed")}
                  columns={columns}
                  selectionMode={SelectionMode.none}
                  layoutMode={DetailsListLayoutMode.justified}
                  isHeaderVisible={true}
                />
              ) : (
                <Stack horizontalAlign="center" className="esign-empty-tab">
                  <Icon iconName="CheckMark" className="esign-empty-icon" />
                  <Text variant="medium">All documents have been signed!</Text>
                </Stack>
              )}
            </PivotItem>
            <PivotItem
              headerText={`Signed (${signedCount})`}
              itemIcon="DocumentApproval"
            >
              {documents.filter((d) => d.status === "Signed").length > 0 ? (
                <DetailsList
                  items={documents.filter((d) => d.status === "Signed")}
                  columns={signedColumns}
                  selectionMode={SelectionMode.none}
                  layoutMode={DetailsListLayoutMode.justified}
                  isHeaderVisible={true}
                />
              ) : (
                <Stack horizontalAlign="center" className="esign-empty-tab">
                  <Icon iconName="EditNote" className="esign-empty-icon" />
                  <Text variant="medium">No signed documents yet.</Text>
                </Stack>
              )}
            </PivotItem>
          </Pivot>
        </div>
      ) : (
        <div className="esign-card esign-no-docs-card">
          <div className="esign-no-docs-icon-wrap">
            <Icon iconName="DocumentSet" className="esign-no-docs-icon" />
          </div>
          <Text variant="xLarge" className="esign-no-docs-title">
            No Documents Required
          </Text>
          <Text variant="medium" className="esign-no-docs-subtitle">
            There are no eSign documents applicable to your account at this
            time.
          </Text>
        </div>
      )}
    </div>
  );
};

export default ESign;
